import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, downloadPdf } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Trash2, Pencil, AlertTriangle, UserPlus, Loader2, ShieldCheck, FileDown } from "lucide-react";
import { ExpiryBadge } from "@/components/ExpiryBadge";
import { toast } from "sonner";

export default function HR() {
  const [drivers, setDrivers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([api.get("/hr/drivers"), api.get("/hr/alerts")]);
      setDrivers(d.data || []);
      setAlerts(a.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return drivers;
    return drivers.filter((d) => `${d.name} ${d.phone} ${d.email} ${d.licence_number}`.toLowerCase().includes(s));
  }, [drivers, q]);

  const importContacts = async () => {
    setImporting(true);
    try {
      const r = await api.post("/hr/drivers/import-contacts");
      toast.success(r.data.created ? `${r.data.created} driver${r.data.created === 1 ? "" : "s"} added` : "All self-employed drivers are already in HR");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const onDelete = async (d) => {
    if (!window.confirm(`Remove ${d.name} and all their HR documents?`)) return;
    try { await api.delete(`/hr/drivers/${d.id}`); toast.success(`${d.name} removed`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="hr-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">People</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1">HR · Drivers</h1>
          <p className="text-slate-600 mt-2 text-sm">Driver documents, expiry dates and driving licence checks in one place.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="hr-import-btn" variant="outline" className="rounded-sm" onClick={importContacts} disabled={importing}>
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Add all self-employed drivers
          </Button>
          <Button data-testid="hr-add-driver-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Add driver
          </Button>
        </div>
      </div>

      <AlertsCard alerts={alerts} />

      <Card className="rounded-sm border border-slate-200 shadow-none">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="hr-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, licence no." className="pl-9 rounded-sm border-slate-300" />
          </div>
          <div className="ml-auto text-xs text-slate-500" data-testid="hr-driver-count">{filtered.length} driver{filtered.length === 1 ? "" : "s"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full po-table text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider">
                <th className="px-3 py-3 font-semibold">Driver</th>
                <th className="px-3 py-3 font-semibold">Licence no.</th>
                <th className="px-3 py-3 font-semibold">Licence expiry</th>
                <th className="px-3 py-3 font-semibold">Next check due</th>
                <th className="px-3 py-3 font-semibold text-center">Points</th>
                <th className="px-3 py-3 font-semibold">Documents</th>
                <th className="px-3 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500" data-testid="hr-empty">No drivers yet. Click &quot;Add driver&quot; or bring in your self-employed drivers.</td></tr>
              )}
              {!loading && filtered.map((d) => <DriverRow key={d.id} d={d} onDelete={onDelete} />)}
            </tbody>
          </table>
        </div>
      </Card>

      <AddDriverDialog open={addOpen} onOpenChange={setAddOpen} linkedIds={new Set(drivers.map((d) => d.contact_id).filter(Boolean))} />
    </div>
  );
}

function AlertsCard({ alerts }) {
  if (!alerts.length) {
    return (
      <Card className="rounded-sm border border-emerald-200 bg-emerald-50/50 shadow-none p-4 flex items-center gap-2 text-sm text-emerald-800" data-testid="hr-alerts-clear">
        <ShieldCheck className="h-4 w-4" /> All clear — nothing expired or expiring in the next 30 days.
      </Card>
    );
  }
  return (
    <Card className="rounded-sm border border-amber-200 shadow-none" data-testid="hr-alerts">
      <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="h-4 w-4" /> {alerts.length} item{alerts.length === 1 ? "" : "s"} expired or expiring within 30 days
      </div>
      <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {alerts.map((a, i) => (
          <li key={`${a.driver_id}-${a.item}-${i}`} data-testid={`hr-alert-${i}`}>
            <Link to={`/hr/drivers/${a.driver_id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
              <span className={`h-2 w-2 rounded-full shrink-0 ${a.tone === "red" ? "bg-red-500" : "bg-amber-500"}`} />
              <span className="font-medium text-slate-900 w-48 truncate">{a.driver_name}</span>
              <span className="text-slate-600 text-sm truncate flex-1">{a.item}</span>
              <span className={`text-xs font-semibold tabular-nums ${a.tone === "red" ? "text-red-600" : "text-amber-700"}`}>
                {a.days < 0 ? `Expired ${-a.days}d ago` : a.days === 0 ? "Today" : `In ${a.days}d`}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function DriverRow({ d, onDelete }) {
  const s = d.doc_stats || {};
  const pts = d.licence_points || 0;
  const ptsTone = pts >= 9 ? "text-red-700 bg-red-50 border-red-200" : pts >= 6 ? "text-amber-800 bg-amber-50 border-amber-200" : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <tr className={`border-t border-slate-100 hover:bg-slate-50 ${d.is_active === false ? "opacity-60" : ""}`} data-testid={`hr-row-${d.id}`}>
      <td className="px-3 py-3">
        <Link to={`/hr/drivers/${d.id}`} className="font-medium text-slate-900 hover:underline">{d.name}</Link>
        <div className="text-xs text-slate-500">{d.phone || d.email || ""}{d.is_active === false ? " · Inactive" : ""}</div>
      </td>
      <td className="px-3 py-3 text-xs text-slate-700 font-mono">{d.licence_number || "—"}</td>
      <td className="px-3 py-3"><ExpiryBadge date={d.licence_expiry} testid={`hr-licence-expiry-${d.id}`} /></td>
      <td className="px-3 py-3"><ExpiryBadge date={d.next_check_due} testid={`hr-next-check-${d.id}`} /></td>
      <td className="px-3 py-3 text-center"><span className={`inline-block border rounded-sm px-2 py-0.5 text-xs font-semibold ${ptsTone}`}>{pts}</span></td>
      <td className="px-3 py-3 text-xs" data-testid={`hr-docs-${d.id}`}>
        <span className="text-slate-700">{s.docs || 0} file{s.docs === 1 ? "" : "s"}</span>
        {s.expired > 0 && <span className="ml-2 text-red-700 font-semibold">{s.expired} expired</span>}
        {s.expiring > 0 && <span className="ml-2 text-amber-700 font-semibold">{s.expiring} expiring</span>}
      </td>
      <td className="px-3 py-3 text-right whitespace-nowrap">
        <Button data-testid={`hr-pdf-${d.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" title="Download full driver file (PDF)"
          onClick={() => downloadPdf(`/hr/drivers/${d.id}/file.pdf`, `driver-file-${d.name.replace(/[^A-Za-z0-9]+/g, "-")}.pdf`)}>
          <FileDown className="h-4 w-4" />
        </Button>
        <Link to={`/hr/drivers/${d.id}`}>
          <Button data-testid={`hr-edit-${d.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm"><Pencil className="h-4 w-4" /></Button>
        </Link>
        <Button data-testid={`hr-delete-${d.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600" onClick={() => onDelete(d)}><Trash2 className="h-4 w-4" /></Button>
      </td>
    </tr>
  );
}

function AddDriverDialog({ open, onOpenChange, linkedIds }) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [name, setName] = useState("");
  const [contactId, setContactId] = useState("none");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(""); setContactId("none");
    api.get("/contacts", { params: { type: "subcontractor" } }).then((r) => setContacts((r.data || []).filter((c) => !linkedIds.has(c.id)))).catch(() => setContacts([]));
  }, [open]); // eslint-disable-line

  const pick = (v) => {
    setContactId(v);
    const c = contacts.find((x) => x.id === v);
    if (c) setName(c.name);
  };

  const save = async () => {
    const c = contacts.find((x) => x.id === contactId);
    setSaving(true);
    try {
      const address = c ? [c.address_line1, c.address_line2, c.city, c.postcode].filter(Boolean).join(", ") : "";
      const r = await api.post("/hr/drivers", {
        name: name.trim(), contact_id: c?.id || null,
        phone: c?.phone || "", email: c?.email || "", address,
      });
      toast.success(`${r.data.name} added`);
      navigate(`/hr/drivers/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid="hr-add-dialog">
        <DialogHeader><DialogTitle>Add driver</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-600">Pick an existing self-employed driver (optional)</Label>
            <Select value={contactId} onValueChange={pick}>
              <SelectTrigger data-testid="hr-add-contact" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— New driver, not in the portal —</SelectItem>
                {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.driver_id ? ` (${c.driver_id})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Driver name</Label>
            <Input data-testid="hr-add-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="mt-1 rounded-sm border-slate-300" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="hr-add-save" className="rounded-sm bg-slate-900 hover:bg-slate-800" disabled={!name.trim() || saving} onClick={save}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add driver
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
